import { Modal, Box, Typography, FormControl, TextField, Button, useTheme, FormLabel, MenuItem, Select, Menu, Input } from "@mui/material";
import React, { useContext, useState } from "react";
import { AIVerificationResult } from "../../../api";
import { assetsApi, inspectionsApi } from "../../../api-interface";
import { TenantContext } from "../../..";
import { Link, useNavigate } from "react-router-dom";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSnackbar } from "notistack";
import { yupResolver } from '@hookform/resolvers/yup';
import { get, useForm } from 'react-hook-form';
import * as yup from "yup"
import UnstyledLink from "../../../components/common/UnstyledLink/UnstyledLink";
import { documentTypeLabels } from '../../../components/common/documentTypeLabels';

const UploadDocumentModal = ({ open, handleClose, assetId }: { open: boolean, handleClose: () => void, assetId: string }) => {
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        minWidth: 100,
        p: 4,
    };
    const theme = useTheme()
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate()
    const tenant = useContext(TenantContext)

    const schema = yup
        .object({
            name: yup.string().required('A document name is required'),
            type: yup.string().required('A document type is required'),
            expiry: yup.date().nullable().notRequired().transform((curr, orig) => orig === '' ? null : curr),
            file: yup
                .mixed()
                .test('required', 'A file is required', (value) => {
                    return value && (Array.isArray(value) ? value.length > 0 : value instanceof FileList ? value.length > 0 : false);
                })
                .test('fileSize', 'The file is too large', (value) => {
                    return value && value[0] && value[0].size <= 10485760; // 10MB
                })
                .test('fileType', 'Unsupported File Format', (value) => {
                    return value && value[0] && ['application/pdf', 'image/jpeg', 'image/png'].includes(value[0].type);
                }),
        })
        .required()

    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        reset
    } = useForm({
        resolver: yupResolver(schema),
    })

        const handleUpload = async (x) => {
        try {
            console.log(x)
            // Format date as YYYY-MM-DD for expiry
            if (x.expiry) {
                x.expiry = new Date(x.expiry).toISOString().split('T')[0];
            } else {
                x.expiry = "";
            }
            await assetsApi(tenant).uploadAssetDocumentAssetsAssetIdDocumentsPost(assetId, x.name, x.type, x.file[0].name, x.expiry, Array.from(x.file))
            enqueueSnackbar("Document Uploaded", { variant: "success" })
            reset();
            navigate("")
            handleClose()
        } catch (e) {
            if (e?.response?.data?.detail) {
                enqueueSnackbar(e.response.data.detail, { variant: "error" })
            } else {
                enqueueSnackbar("Error: " + e, { variant: "error" })
            }
        }
    }

    const selectFile = () => {
        document.getElementById("fileInput")?.click()
    }

    const selectedFile = watch("file") as FileList | undefined;
    const tenant_id = useContext(TenantContext)

    return <Modal
        open={open}
        onClose={() => {
            handleClose()
        }}
    >
        <Box sx={style} display="flex" flexDirection={"column"} gap={2}>
            <Typography variant="h6">Upload Asset File</Typography>
            <Box display="flex" flexDirection={"column"} gap={2}>
                <FormControl fullWidth>
                    <TextField
                        label="Document Name"
                        {...register("name")}
                        error={!!errors.name}
                        helperText={errors.name ? errors.name.message : ''}
                    />
                </FormControl>
                <FormControl fullWidth>
                    <FormLabel>Document Type<Link to={"/dashboard/" + tenant_id + "/inspections/"}><Typography sx={{float:"right", "display":"inline", marginLeft: "20px"}}>LGSR? Upload at inspections</Typography></Link></FormLabel>
                    <Select
                        label="Document Type"
                        defaultValue=""
                        {...register("type")}
                        error={!!errors.type}
                    >
                        <MenuItem value="" disabled>Select Type</MenuItem>
                        {Object.entries(documentTypeLabels).map(([value, label]) => (
                            <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))}
                    </Select>
                    {errors.type && <Typography variant="body2" color={theme.palette.error.main}>{errors.type.message}</Typography>}
                </FormControl>
                <FormControl fullWidth>
                    <TextField
                        label="Expiry Date"
                        type="date"
                        {...register("expiry")}
                        slotProps={{
                            inputLabel: { shrink: true },
                        }}
                        sx={{ width: '100%' }}
                        error={!!errors.expiry}
                        helperText={errors.expiry ? errors.expiry.message : 'Optional'}
                    />
                </FormControl>
                <FormControl fullWidth>
                    <Typography>
                            {selectedFile && selectedFile[0] && selectedFile[0].name}
                    </Typography>
                    <Button onClick={selectFile} variant="outlined" component="label" color={errors.file ? "error" : "primary"} sx={{ minWidth: 180 }}>
                        {errors.file ? <><ErrorOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Browse Files</> : 'Browse Files'}
                    </Button>
                    <Input
                        type="file"
                        style={{ display: 'none' }}
                        id="fileInput"
                        {...register("file")}
                        inputProps={{ accept: ".pdf,.png,.jpg,.jpeg,.doc,.docx,.txt,.xls,.xlsx,.ppt,.pptx,.csv" }}
                    />

                    {errors.file && <Typography variant="body2" color={theme.palette.error.main}>{errors.file.message}</Typography>}
                </FormControl>
            </Box>
            <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                    variant="outlined"
                    onClick={() => {
                        reset();
                        handleClose()
                    }}
                >Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit(handleUpload)}
                >Upload</Button>
            </Box>
        </Box>
    </Modal>
}
export default UploadDocumentModal